import queryString from 'query-string'
import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import store from 'store'

import authentication from 'api/authentication'
import companies from 'api/company'
import Email from 'components/authentication/form-fields/Email'
import SetPassword from 'components/authentication/form-fields/SetPassword'
import passwordOutcome from 'components/authentication/passwordOutcome'
import AgreeToTerms from 'components/authentication/registration/register-company/AgreeToTerms'
import CompanyName from 'components/authentication/registration/register-company/CompanyName'
import { SignedOutForm } from 'components/authentication/SignedOutForm'
import SignedOutFormContainer from 'components/authentication/SignedOutFormContainer'
import { UpperBackText } from 'components/authentication/UpperBackText'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { identify, track } from 'track'

const FORM_STAGES = {
  EMAIL: 'email',
  COMPANY: 'companyName',
  PASSWORD: 'password',
  AGREE_TO_TERMS: 'agreeToTerms'
}

const FORM_STAGE_DETAILS = {
  email: {
    title: 'Register',
    nextStage: FORM_STAGES.COMPANY
  },
  companyName: {
    back: true,
    title: 'Enter your company name',
    nextStage: FORM_STAGES.PASSWORD,
    backStage: FORM_STAGES.EMAIL
  },
  password: {
    back: true,
    title: 'Set your password',
    nextStage: FORM_STAGES.AGREE_TO_TERMS,
    backStage: FORM_STAGES.COMPANY
  },
  agreeToTerms: {
    back: true,
    title: 'Agree to terms',
    backStage: FORM_STAGES.PASSWORD
  }
}

function trackSubmission(email: string) {
  identify(email)
  track('RegistrationSubmission', {
    role: 'super',
    userId: email
  })
}

export const Register: React.FC<{
  startTrial?: (params: Record<string, any>) => void
}> = ({ startTrial = companies.startTrial }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const authState = useAuth()

  const parsed = queryString.parse(location.search)

  const [state, setState] = useState({
    code: parsed.code,
    companyId: '',
    companySecret: '',
    confirmPassword: '',
    email: parsed.email || '',
    evaluation: false,
    name: '',
    password: '',
    submitting: false,
    formStage: 'email',
    privacyPolicyAccepted: false
  })

  const updateState = (newState) => {
    setState((prevState) => ({ ...prevState, ...newState }))
  }

  const handleResponse = (type, text) => {
    updateState({ submitting: false })
    toast({ type, text })
  }

  const handleChange = ({ target }) => {
    updateState({
      [target.name]: target.value
    })
  }

  const handleEnabledChanged = (event) => {
    updateState({
      evaluation: Boolean(event.target.checked)
    })
  }

  const submitDetails = async (event) => {
    event.preventDefault()
    const { name, email: adminEmail, password: adminPassword, code } = state

    const handleError = (response) => {
      handleResponse(
        'error',
        response.data || 'There was an issue, Please try again later'
      )
    }
    updateState({ submitting: true })

    let trialResponse
    try {
      trialResponse = await startTrial({
        name,
        adminEmail,
        adminPassword,
        code,
        allowUsStorage: true
      })
    } catch (err) {
      handleError(err.response)
    }

    if (!trialResponse) return
    // Sets local storage company for login.
    store.set('company', { name, methods: ['password'] })

    trackSubmission(adminEmail)

    updateState({
      registered: true,
      companyId: trialResponse.identifier,
      companySecret: trialResponse.secret
    })

    handleResponse('success', 'Company Registered')

    let credentials
    try {
      credentials = await authentication.signin({
        email: adminEmail,
        companyName: name,
        password: adminPassword
      })
    } catch (err) {
      handleError(err.response)
    }

    if (!credentials) {
      return navigate('/')
    }

    await authState.startSession(credentials)

    navigate('/')
  }

  const handleBack = () => {
    const backStage = FORM_STAGE_DETAILS[state.formStage].backStage
    updateState({ formStage: backStage })
  }

  const submitForm = (event) => {
    event.preventDefault()
    const nextStage = FORM_STAGE_DETAILS[state.formStage].nextStage
    const { EMAIL, COMPANY, PASSWORD, AGREE_TO_TERMS } = FORM_STAGES

    switch (state.formStage) {
      case EMAIL:
      case COMPANY:
      case PASSWORD:
        updateState({ formStage: nextStage })
        break
      case AGREE_TO_TERMS:
        submitDetails(event)
        break
    }
  }

  const handleAcceptPrivacyPolicyChange = () => {
    updateState({
      privacyPolicyAccepted: !state.privacyPolicyAccepted
    })
  }

  const formContent = () => {
    const { EMAIL, COMPANY, PASSWORD, AGREE_TO_TERMS } = FORM_STAGES

    switch (state.formStage) {
      case EMAIL:
        return <Email email={state.email} inputIsDisabled={true} />
      case COMPANY:
        return (
          <CompanyName companyName={state.name} handleChange={handleChange} />
        )
      case PASSWORD:
        return (
          <SetPassword
            confirmPassword={state.confirmPassword}
            handleAcceptPrivacyPolicyChange={handleAcceptPrivacyPolicyChange}
            handleChange={handleChange}
            isLoading={state.submitting}
            password={state.password}
            passwordOutcome={passwordOutcome}
            privacyPolicyAccepted={state.privacyPolicyAccepted}
          />
        )
      case AGREE_TO_TERMS:
        return (
          <AgreeToTerms
            evaluation={state.evaluation}
            handleEnabledChanged={handleEnabledChanged}
            isLoading={state.submitting}
          />
        )
    }
  }

  const { title, back } = FORM_STAGE_DETAILS[state.formStage]
  return (
    <SignedOutFormContainer data-test-submit-registration>
      <UpperBackText onClick={back && handleBack}>Back to login</UpperBackText>
      <SignedOutForm title={title} onSubmit={submitForm}>
        {formContent()}
      </SignedOutForm>
    </SignedOutFormContainer>
  )
}
